import { useEffect, useMemo, useState } from "react";
import { ActionButton, GlassPanel, TurnstileWidget } from "@guandan/ui";
import {
  claimNicknamePassword,
  getNicknameStatus,
  loginWithNickname,
} from "../lib/nickname-auth";
import { requestOtp, verifyOtp } from "../lib/otp";
import type { PlayerSession } from "../lib/session";
import heroUrl from "../../../../assets/scenes/modern-lounge-haikou-table-empty-v3.webp";

const OWNER_EMAIL = "45088195@qq.com";
type AuthMode = "nickname" | "password" | "owner-send" | "owner-code";

export function AuthFlow({
  onVerified,
}: {
  onVerified: (session: PlayerSession) => void | Promise<void>;
}) {
  const [mode, setMode] = useState<AuthMode>("nickname");
  const [nickname, setNickname] = useState("");
  const [registered, setRegistered] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [code, setCode] = useState("");
  const [expiresAt, setExpiresAt] = useState(0);
  const [resendAt, setResendAt] = useState(0);
  const [developmentCode, setDevelopmentCode] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const siteKey =
    (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)?.trim() ??
    "";
  const resendSeconds = Math.max(0, Math.ceil((resendAt - now) / 1_000));
  const expiresLabel = useMemo(
    () =>
      expiresAt
        ? new Date(expiresAt).toLocaleTimeString("zh-CN", {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "",
    [expiresAt],
  );

  useEffect(() => {
    if (mode !== "owner-code") return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [mode]);

  async function continueWithNickname() {
    setBusy(true);
    setError("");
    try {
      const status = await getNicknameStatus(nickname);
      setNickname(status.nickname);
      setRegistered(status.registered);
      setPassword("");
      setPasswordConfirmation("");
      setMode("password");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "昵称检查失败");
    } finally {
      setBusy(false);
    }
  }

  async function enterWithPassword() {
    if (!registered && password !== passwordConfirmation) {
      setError("两次输入的密码不一致");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await loginWithNickname(nickname, password);
      await onVerified(result.session);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "昵称登录失败");
    } finally {
      setBusy(false);
    }
  }

  async function sendOwnerCode() {
    if (siteKey && !captchaToken) {
      setError("请先完成人机验证");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await requestOtp(OWNER_EMAIL, captchaToken ?? undefined);
      setExpiresAt(result.expiresAt);
      setResendAt(result.resendAt);
      setDevelopmentCode(result.developmentCode ?? null);
      setCode("");
      setNow(Date.now());
      setMode("owner-code");
      if (siteKey) {
        setCaptchaToken(null);
        setCaptchaReset((value) => value + 1);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "验证码发送失败");
    } finally {
      setBusy(false);
    }
  }

  async function verifyOwnerCode() {
    setBusy(true);
    setError("");
    try {
      await onVerified(await verifyOtp(OWNER_EMAIL, code));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "验证失败");
    } finally {
      setBusy(false);
    }
  }

  function backToNickname() {
    setMode("nickname");
    setPassword("");
    setPasswordConfirmation("");
    setError("");
  }

  return (
    <main
      className="auth-screen"
      style={{
        backgroundImage: `linear-gradient(90deg, rgba(3,14,25,.88), rgba(3,14,25,.28)), url(${heroUrl})`,
      }}
    >
      <div className="auth-screen__brand">
        <div>
          <h1>掼蛋高手</h1>
          <p>好友同桌，默契对家</p>
        </div>
      </div>
      <GlassPanel className="auth-card">
        {mode === "nickname" ? (
          <>
            <h2>昵称登录</h2>
            <p className="muted">
              每个昵称只能注册一次，战绩、等级和好友记录都会保留。
            </p>
            <label className="field">
              <span>昵称</span>
              <input
                autoFocus
                maxLength={12}
                autoComplete="username"
                value={nickname}
                onChange={(event) => setNickname(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") void continueWithNickname();
                }}
                placeholder="2—12 个字符"
              />
            </label>
            {error ? (
              <p className="form-error" role="alert">
                {error}
              </p>
            ) : null}
            <ActionButton
              tone="orange"
              size="lg"
              wide
              disabled={busy || nickname.normalize("NFKC").trim().length < 2}
              onClick={() => void continueWithNickname()}
            >
              {busy ? "正在检查…" : "继续"}
            </ActionButton>
            <button
              className="text-button"
              type="button"
              onClick={() => {
                setMode("owner-send");
                setError("");
              }}
            >
              业主邮箱验证与语音房权限
            </button>
          </>
        ) : null}

        {mode === "password" ? (
          <>
            <h2>{registered ? "输入登录密码" : "设置登录密码"}</h2>
            <p className="muted">
              {registered
                ? `正在登录“${nickname}”，密码用于防止他人冒用你的昵称。`
                : `“${nickname}”尚未注册。设置密码后，今后使用昵称和密码登录。`}
            </p>
            <label className="field">
              <span>{registered ? "密码" : "设置密码"}</span>
              <input
                autoFocus
                type="password"
                minLength={8}
                maxLength={64}
                autoComplete={registered ? "current-password" : "new-password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && registered)
                    void enterWithPassword();
                }}
                placeholder="8—64 个字符"
              />
            </label>
            {!registered ? (
              <label className="field">
                <span>确认密码</span>
                <input
                  type="password"
                  minLength={8}
                  maxLength={64}
                  autoComplete="new-password"
                  value={passwordConfirmation}
                  onChange={(event) =>
                    setPasswordConfirmation(event.target.value)
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter") void enterWithPassword();
                  }}
                  placeholder="再次输入密码"
                />
              </label>
            ) : null}
            {error ? (
              <p className="form-error" role="alert">
                {error}
              </p>
            ) : null}
            <ActionButton
              tone="orange"
              size="lg"
              wide
              disabled={
                busy ||
                password.length < 8 ||
                (!registered && passwordConfirmation.length < 8)
              }
              onClick={() => void enterWithPassword()}
            >
              {busy
                ? registered
                  ? "正在登录…"
                  : "正在注册…"
                : registered
                  ? "登录"
                  : "注册并进入"}
            </ActionButton>
            <button
              className="text-button"
              type="button"
              onClick={backToNickname}
            >
              更换昵称
            </button>
          </>
        ) : null}

        {mode === "owner-send" ? (
          <>
            <h2>业主身份验证</h2>
            <p className="muted">
              验证码将发送至 {OWNER_EMAIL}。只有该账号可以创建调用 LiveKit
              的语音房。
            </p>
            {siteKey ? (
              <TurnstileWidget
                siteKey={siteKey}
                resetKey={captchaReset}
                onToken={setCaptchaToken}
                onError={setError}
              />
            ) : null}
            {error ? (
              <p className="form-error" role="alert">
                {error}
              </p>
            ) : null}
            <ActionButton
              tone="orange"
              size="lg"
              wide
              disabled={busy || Boolean(siteKey && !captchaToken)}
              onClick={() => void sendOwnerCode()}
            >
              {busy ? "正在发送…" : "发送验证码"}
            </ActionButton>
            <button
              className="text-button"
              type="button"
              onClick={backToNickname}
            >
              返回昵称登录
            </button>
          </>
        ) : null}

        {mode === "owner-code" ? (
          <>
            <h2>输入六位验证码</h2>
            <p className="muted">
              验证码已发送至 {OWNER_EMAIL}，{expiresLabel} 前有效。
            </p>
            <label className="field">
              <span>六位验证码</span>
              <input
                autoFocus
                className="otp-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(event) =>
                  setCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter" && code.length === 6)
                    void verifyOwnerCode();
                }}
              />
            </label>
            {developmentCode ? (
              <p className="dev-code">
                本地验证码：<strong>{developmentCode}</strong>
              </p>
            ) : null}
            {error ? (
              <p className="form-error" role="alert">
                {error}
              </p>
            ) : null}
            <ActionButton
              tone="orange"
              size="lg"
              wide
              disabled={busy || code.length !== 6}
              onClick={() => void verifyOwnerCode()}
            >
              {busy ? "正在验证…" : "验证并进入"}
            </ActionButton>
            <div className="button-row">
              <ActionButton
                tone="ghost"
                size="sm"
                disabled={busy || resendSeconds > 0}
                onClick={() => void sendOwnerCode()}
              >
                {resendSeconds > 0 ? `${resendSeconds} 秒后重发` : "重新发送"}
              </ActionButton>
              <button
                className="text-button"
                type="button"
                onClick={backToNickname}
              >
                返回昵称登录
              </button>
            </div>
          </>
        ) : null}
        <p className="auth-card__legal">
          继续即表示你同意公平游戏规则与隐私说明。语音默认不录音、不保存。
        </p>
      </GlassPanel>
    </main>
  );
}

export function LegacyNicknamePasswordSetup({
  nickname,
  onCompleted,
}: {
  nickname: string;
  onCompleted: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    if (password !== confirmation) {
      setError("两次输入的密码不一致");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await claimNicknamePassword(password);
      onCompleted();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "密码设置失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main
      className="auth-screen"
      style={{
        backgroundImage: `linear-gradient(90deg, rgba(3,14,25,.88), rgba(3,14,25,.28)), url(${heroUrl})`,
      }}
    >
      <div className="auth-screen__brand">
        <div>
          <h1>掼蛋高手</h1>
          <p>好友同桌，默契对家</p>
        </div>
      </div>
      <GlassPanel className="auth-card">
        <h2>为原昵称设置密码</h2>
        <p className="muted">
          这是旧版账号的一次性安全升级。原昵称“{nickname}”及全部战绩会继续保留。
        </p>
        <label className="field">
          <span>设置密码</span>
          <input
            autoFocus
            type="password"
            minLength={8}
            maxLength={64}
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        <label className="field">
          <span>确认密码</span>
          <input
            type="password"
            minLength={8}
            maxLength={64}
            autoComplete="new-password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void submit();
            }}
          />
        </label>
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        <ActionButton
          tone="orange"
          size="lg"
          wide
          disabled={busy || password.length < 8 || confirmation.length < 8}
          onClick={() => void submit()}
        >
          {busy ? "正在保存…" : "保存密码并进入"}
        </ActionButton>
      </GlassPanel>
    </main>
  );
}
